import { uniq } from 'lodash'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import { BrokerBuySteps } from '@/enums/brokerBuySteps'

interface IFormStore {
  step: number
  locationRerun: number
  stepAsEnum: BrokerBuySteps
  stepPath: BrokerBuySteps[]
  formData: any
  quotes: any
  setQuote: (state: any) => void
  setData: (state: any) => void
  setStep: (step: BrokerBuySteps) => void
  CCReset: () => void
  setLocationReRun: (locationRerun: number) => void
}

interface IBrokerGuid {
  guid?: string
  setGuid: (guid?: string) => void
  guidReset: () => void
}

interface IClient {
  clientId?: string
  setClient: (clientId?: string) => void
  clientReset: () => void
}

interface IBackoffice {
  backoffice?: boolean
  setBackoffice: (backoffice?: boolean) => void
  backofficeReset: () => void
}

interface IMta {
  mta?: boolean
  setMta: (mta?: boolean) => void
  mtaReset: () => void
}

interface IBrokerDetail {
  broker?: {
    brokerName: string
    brokerId: string
  }
  setBroker: (broker?: any) => void
  brokerReset: () => void
}

interface IOptions {
  options?: {
    activities: { value: number; text: string }[]
    coverTypes: { value: number; text: string; extra: string }[]
    plIndemnityLevels: { value: number; text: string }[]
    elIndemnityLevels: { value: number; text: string }[]
    indemnityPeriod: { value: number; text: string }[]
    basisIfCover: { value: number; text: string }[]
    insuredTradeTypes: { value: number; text: string }[]
    premisesCategories: { value: number; text: string }[]
    intruderAlarmTypes: { value: number; text: string }[]
    titles: { value: number; text: string }[]
    businessTypes: { isCompany: boolean; value: number; text: string }[]
    claimTypes: { value: number; text: string }[]
    propertyClaims: { value: number; text: string }[]
    liabilityClaims: { value: number; text: string }[]
    currentInsurers: { value: number; text: string }[]
    riskDetails: string[]
    followingPractice: string[]
  }
  setOptions: (options: any) => void
}

export interface OptionsType {
  isCompany: boolean
  text: string
  value: number
}

const substrStepEnum = (step: BrokerBuySteps): number =>
  Number(step.substring(4))

export const commercialCombinedFormStore = create<IFormStore>()(
  persist(
    (set, get) => ({
      stepAsEnum: BrokerBuySteps.Step1,
      step: substrStepEnum(get()?.stepAsEnum || BrokerBuySteps.Step1),
      stepPath: [BrokerBuySteps.Step1],
      locationRerun: 0,
      setLocationReRun: (newRerun: any) =>
        set(() => ({
          locationRerun: newRerun,
        })),
      formData: {},
      quotes: {},

      setQuote: (newQuote: any) =>
        set((state: any) => ({
          quotes: { ...state.quotes, ...newQuote },
        })),
      setData: (newData: any) =>
        set((state: any) => ({
          formData: { ...state.formData, ...newData },
        })),
      setStep: (step: BrokerBuySteps) =>
        set((state) => ({
          step: substrStepEnum(step),
          stepAsEnum: step,
          stepPath: uniq([...state.stepPath, step]),
        })),
      CCReset: () => {
        set(() => ({
          formData: {},
          quotes: {},
          stepAsEnum: BrokerBuySteps.Step1,
          step: substrStepEnum(get()?.stepAsEnum || BrokerBuySteps.Step1),
        }))
      },
    }),
    {
      name: 'commercialCombined',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)
export const brokerGuidStore = create<IBrokerGuid>()(
  persist(
    (set) => ({
      guid: undefined,
      setGuid(guid) {
        set(() => ({ guid: guid }))
      },
      guidReset: () => set(() => ({ guid: undefined })),
    }),
    {
      name: 'brokerGuid',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const isBackofficeStore = create<IBackoffice>()(
  persist(
    (set) => ({
      backoffice: false,
      setBackoffice(backoffice) {
        set(() => ({ backoffice: backoffice }))
      },
      backofficeReset: () => set(() => ({ backoffice: false })),
    }),
    {
      name: 'isBackoffice',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const isMTAStore = create<IMta>()(
  persist(
    (set) => ({
      mta: false,
      setMta(mta) {
        set(() => ({ mta: mta }))
      },
      mtaReset: () => set(() => ({ mta: false })),
    }),
    {
      name: 'isMta',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const clientStore = create<IClient>()(
  persist(
    (set) => ({
      clientId: undefined,
      setClient(clientId) {
        set(() => ({ clientId: clientId }))
      },
      clientReset: () => set(() => ({ clientId: undefined })),
    }),
    {
      name: 'clientId',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const brokerDetailStore = create<IBrokerDetail>()(
  persist(
    (set) => ({
      broker: undefined,
      setBroker(broker) {
        set(() => ({
          broker: {
            brokerId: broker.brokerId,
            brokerName: broker.brokerName,
          },
        }))
      },
      brokerReset: () => set(() => ({ broker: undefined })),
    }),
    {
      name: 'brokerDetail',
      storage: createJSONStorage(() => sessionStorage),
    }
  )
)

export const commercialCombinedOptionsFormStore = create<IOptions>()((set) => ({
  options: undefined,
  /**
   * Merges new options with the existing options in the store state.
   * @param {any} options The new options to merge.
   * @returns {void}
   */
  setOptions: (options: any) =>
    set((state) => ({ options: { ...state.options, ...options } })),
}))
